/* ============================================================
   earKART Cinematic — global animation framework
   Mirrors the prototype motion system 1:1:
   - intro curtain (logo + halo ignite, lifts after hold or first input)
   - [data-reveal] fade + rise + blur-in (hero staggered, rest on scroll)
   - [data-beat] line-by-line mask reveal
   - [data-motes] drifting cyan particles
   - nav background solidify on scroll
   All of it respects prefers-reduced-motion and re-initializes on
   Shopify theme-editor events so the customizer preview stays live.
   ============================================================ */
(function () {
  'use strict';

  var EK = (window.EK = window.EK || {});
  EK.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  EK.designMode = !!(window.Shopify && window.Shopify.designMode);

  var registry = {};
  EK.register = function (name, fn) { registry[name] = fn; };

  /* ---------- intro curtain ---------- */
  function initIntro() {
    var intro = document.getElementById('ek-intro');
    if (!intro) { heroReveal(document); return; }

    var hold = parseInt(intro.getAttribute('data-hold'), 10) || 2200;
    var once = intro.getAttribute('data-frequency') === 'session';
    var seen = false;
    try { seen = once && sessionStorage.getItem('ek-intro-seen') === '1'; } catch (e) {}

    function dismiss() {
      if (intro.classList.contains('gone')) return;
      intro.classList.add('gone');
      try { if (once) sessionStorage.setItem('ek-intro-seen', '1'); } catch (e) {}
      heroReveal(document);
    }

    if (EK.reduced || EK.designMode || seen) {
      intro.classList.add('gone');
      heroReveal(document);
      return;
    }
    setTimeout(dismiss, hold);
    window.addEventListener('wheel', dismiss, { once: true, passive: true });
    window.addEventListener('touchstart', dismiss, { once: true, passive: true });
    window.addEventListener('click', dismiss, { once: true });
  }

  /* ---------- hero (above the fold) staggered reveal ---------- */
  function heroReveal(root) {
    var els = root.querySelectorAll('[data-reveal-hero]:not(.in)');
    els.forEach(function (el, i) {
      // Sections may tune the stagger (prototypes vary 130-160ms) via a
      // data-hero-stagger attribute on any ancestor of the hero elements.
      var host = el.closest('[data-hero-stagger]');
      var step = host ? parseInt(host.getAttribute('data-hero-stagger'), 10) || 160 : 160;
      setTimeout(function () { el.classList.add('in'); }, i * step);
    });
  }
  EK.heroReveal = heroReveal;

  /* ---------- scroll reveal ---------- */
  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
    });
  }, { threshold: 0.14 });

  /* ---------- type-beat lines ---------- */
  var beatIo = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      if (!e.isIntersecting) return;
      var lines = e.target.querySelectorAll('.beat-line');
      lines.forEach(function (l, i) { setTimeout(function () { l.classList.add('in'); }, i * 140); });
      beatIo.unobserve(e.target);
    });
  }, { threshold: 0.4 });

  /* ---------- motes ---------- */
  function initMotes(host) {
    if (EK.reduced) return;
    if (host.getAttribute('data-motes-done')) return;
    host.setAttribute('data-motes-done', '1');
    var count = parseInt(host.getAttribute('data-motes'), 10) || 16;
    for (var i = 0; i < count; i++) {
      var m = document.createElement('i');
      var s = 1 + Math.random() * 2.5;
      m.style.left = Math.random() * 100 + '%';
      m.style.top = 60 + Math.random() * 40 + '%';
      m.style.width = m.style.height = s + 'px';
      m.style.animationDuration = 7 + Math.random() * 8 + 's';
      m.style.animationDelay = Math.random() * 10 + 's';
      host.appendChild(m);
    }
  }

  /* ---------- nav solidify on scroll ---------- */
  var navScrollHandler = null;
  function initNav() {
    var nav = document.querySelector('[data-ek-nav]');
    if (!nav) return;
    var solid = nav.getAttribute('data-solid-bg') || 'rgba(6,4,18,0.92)';
    var soft = nav.getAttribute('data-soft-bg') || 'linear-gradient(180deg, rgba(6,4,18,0.82), transparent)';
    // Single persistent listener: re-inits (theme editor section reloads)
    // replace the handler instead of stacking duplicates on old nav nodes.
    if (navScrollHandler) window.removeEventListener('scroll', navScrollHandler);
    navScrollHandler = function () { nav.style.background = window.scrollY > 60 ? solid : soft; };
    window.addEventListener('scroll', navScrollHandler, { passive: true });
    navScrollHandler();
  }

  /* ---------- init / re-init ---------- */
  EK.init = function (root) {
    root = root || document;

    root.querySelectorAll('[data-reveal]:not([data-reveal-hero])').forEach(function (el) {
      if (!el.classList.contains('in')) io.observe(el);
    });
    root.querySelectorAll('[data-beat]').forEach(function (el) { beatIo.observe(el); });
    root.querySelectorAll('[data-motes]').forEach(initMotes);

    // Section components registered by page scripts (data-ek-component="name")
    root.querySelectorAll('[data-ek-component]').forEach(function (el) {
      var name = el.getAttribute('data-ek-component');
      if (registry[name] && !el.__ekInit) { el.__ekInit = true; registry[name](el); }
    });

    // If root is a freshly (re)rendered section that contains hero reveals, show them
    if (root !== document) heroReveal(root);
  };

  function boot() {
    initIntro();
    initNav();
    EK.init(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* ---------- Shopify theme editor support ---------- */
  document.addEventListener('shopify:section:load', function (e) {
    EK.init(e.target);
    initNav();
    // reveal everything in the reloaded section so editors see content immediately
    e.target.querySelectorAll('[data-reveal]').forEach(function (el) { el.classList.add('in'); });
    e.target.querySelectorAll('.beat-line').forEach(function (el) { el.classList.add('in'); });
  });
  document.addEventListener('shopify:section:select', function (e) {
    e.target.querySelectorAll('[data-reveal]').forEach(function (el) { el.classList.add('in'); });
    e.target.querySelectorAll('.beat-line').forEach(function (el) { el.classList.add('in'); });
  });
})();
